import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { resetOnLogout } from '~/reducers/common-actions';
import type { RootState } from '~/store';
import { isInteger } from 'lodash';
import { isValidFilterMapMode } from './utils';
import type {
    RoutesDrawerFilterState,
    RoutesDrawerMapModeFilterSetting,
    RoutesDrawerMapModeFilterMetricPayload,
    RoutesDrawerMapModeFilterCountPayload
} from './types';
import { defaultRoutesDrawerFilter } from './types';

export const routesDrawerFilterSlice = createSlice({
    name: 'routesDrawerFilter',
    initialState: defaultRoutesDrawerFilter,
    reducers: {
        setMapModeFilterMetric: (
            state: RoutesDrawerFilterState,
            action: PayloadAction<RoutesDrawerMapModeFilterMetricPayload>
        ) => {
            const { mapMode, filterMetric } = action.payload;
            const isValidMapMode = isValidFilterMapMode(mapMode);
            if (!isValidMapMode) {
                return state;
            }
            const updatedMapModeSetting: RoutesDrawerMapModeFilterSetting = {
                ...state[mapMode],
                filterMetric
            };
            return {
                ...state,
                [mapMode]: updatedMapModeSetting
            };
        },
        setMapModeFilteredResultsCount: (
            state: RoutesDrawerFilterState,
            action: PayloadAction<RoutesDrawerMapModeFilterCountPayload>
        ) => {
            const { mapMode, count } = action.payload;
            const isValidMapMode = isValidFilterMapMode(mapMode);
            const isPayloadInteger = isInteger(count);
            if (!isValidMapMode || !isPayloadInteger) {
                return state;
            }
            const updatedMapModeSetting: RoutesDrawerMapModeFilterSetting = {
                ...state[mapMode],
                filteredResultsCount: count
            };
            return {
                ...state,
                [mapMode]: updatedMapModeSetting
            };
        },
        setMapModeUnfilteredTotalCount: (
            state: RoutesDrawerFilterState,
            action: PayloadAction<RoutesDrawerMapModeFilterCountPayload>
        ) => {
            const { mapMode, count } = action.payload;
            const isValidMapMode = isValidFilterMapMode(mapMode);
            const isPayloadInteger = isInteger(count);
            if (!isValidMapMode || !isPayloadInteger) {
                return state;
            }
            const updatedMapModeSetting: RoutesDrawerMapModeFilterSetting = {
                ...state[mapMode],
                unfilteredTotalCount: count
            };
            return {
                ...state,
                [mapMode]: updatedMapModeSetting
            };
        },
        resetRoutesDrawerFilter: (): RoutesDrawerFilterState => {
            return defaultRoutesDrawerFilter;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetOnLogout, () => {
            return defaultRoutesDrawerFilter;
        });
    }
});

export const {
    setMapModeFilterMetric,
    setMapModeFilteredResultsCount,
    setMapModeUnfilteredTotalCount,
    resetRoutesDrawerFilter
} = routesDrawerFilterSlice.actions;

export const selectMapModeFilterMetric =
    (mapMode: string): ((state: RootState) => string[]) =>
    (state: RootState) => {
        const isValidMapMode = isValidFilterMapMode(mapMode);
        if (!isValidMapMode) return [];
        return state.routesDrawerFilter[mapMode].filterMetric;
    };

export const selectMapModeFilteredResultsCount =
    (mapMode: string): ((state: RootState) => number) =>
    (state: RootState) => {
        const isValidMapMode = isValidFilterMapMode(mapMode);
        if (!isValidMapMode) return 0;
        return state.routesDrawerFilter[mapMode].filteredResultsCount;
    };

export const selectMapModeUnfilteredTotalCount =
    (mapMode: string): ((state: RootState) => number) =>
    (state: RootState) => {
        const isValidMapMode = isValidFilterMapMode(mapMode);
        if (!isValidMapMode) return 0;
        return state.routesDrawerFilter[mapMode].unfilteredTotalCount;
    };

export const selectMapModeIsFiltered =
    (mapMode: string): ((state: RootState) => boolean) =>
    (state: RootState) => {
        const isValidMapMode = isValidFilterMapMode(mapMode);
        return (
            isValidMapMode &&
            Boolean(state.routesDrawerFilter[mapMode].filterMetric.length)
        );
    };

export default routesDrawerFilterSlice.reducer;
